/**
 * Subscriptions & Reconciliation API client.
 *
 * Read + resolve for variances and exceptions, plus the control-dashboard
 * tiles. Mirrors the pure-fetch service pattern (relative /api/* URLs proxied
 * to sm4rt-api; types from @techgitter/contracts).
 */

import type {
  ReconciliationDashboard,
  ReconciliationVariance,
  ReconciliationVarianceListResponse,
  DeferSuggestion,
  ReconciliationException,
  ReconciliationExceptionListResponse,
  ResolveVarianceRequest,
  ResolveExceptionRequest,
  ReconciliationStatus,
  VarianceType,
  ExceptionType,
  VendorImportRow,
  VendorImportRowListResponse,
  VendorImportRenewalRow,
  VendorImportRenewalRowListResponse,
  VendorImportBatch,
  VendorImportBatchListResponse,
  UnmatchedWorklist,
  ImportRowMatchStatus,
} from '@techgitter/contracts';

const BASE = '/api/reconciliation';

async function readJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      /* ignore */
    }
    throw new Error(`${op} failed (${response.status} ${response.statusText})${detail}`);
  }
  return (await response.json()) as T;
}

export interface VarianceFilters {
  status?: ReconciliationStatus;
  type?: VarianceType;
  customerId?: string;
  vendorId?: string;
  vendorProductId?: string;
  /** Keep only rows with an Action Date on/after this (YYYY-MM-DD); status=resolved => deferred. */
  actionDateFrom?: string;
}

export interface ExceptionFilters {
  status?: ReconciliationStatus;
  type?: ExceptionType;
  vendorId?: string;
}

export async function fetchReconciliationDashboard(
  signal?: AbortSignal,
): Promise<ReconciliationDashboard> {
  const response = await fetch(`${BASE}/dashboard`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  return readJson<ReconciliationDashboard>(response, 'Load reconciliation dashboard');
}

export async function fetchVariances(
  filters: VarianceFilters = {},
  signal?: AbortSignal,
): Promise<ReconciliationVariance[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.customerId) params.set('customerId', filters.customerId);
  if (filters.vendorId) params.set('vendorId', filters.vendorId);
  if (filters.vendorProductId) params.set('vendorProductId', filters.vendorProductId);
  if (filters.actionDateFrom) params.set('actionDateFrom', filters.actionDateFrom);
  const qs = params.toString();
  const response = await fetch(`${BASE}/variances${qs ? `?${qs}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<ReconciliationVarianceListResponse>(response, 'Load variances');
  return body.items ?? [];
}

export async function resolveVariance(
  id: string,
  request: ResolveVarianceRequest,
  signal?: AbortSignal,
): Promise<ReconciliationVariance> {
  const response = await fetch(`${BASE}/variances/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<ReconciliationVariance>(response, 'Resolve variance');
}

/** Cancel a deferral/resolution — status back to open, resolution + action date
 *  cleared, so the break returns to the active worklist. Admin only. */
export async function reopenVariance(
  id: string,
  signal?: AbortSignal,
): Promise<ReconciliationVariance> {
  const response = await fetch(
    `${BASE}/variances/${encodeURIComponent(id)}/reopen`,
    { method: 'POST', headers: { Accept: 'application/json' }, signal },
  );
  return readJson<ReconciliationVariance>(response, 'Reopen variance');
}

/** Suggested Defer action date for a term-conversion break (annual invoice
 *  next-run + buffer) plus context. */
export async function fetchDeferSuggestion(
  id: string,
  signal?: AbortSignal,
): Promise<DeferSuggestion> {
  const response = await fetch(
    `${BASE}/variances/${encodeURIComponent(id)}/defer-suggestion`,
    { headers: { Accept: 'application/json' }, signal },
  );
  return readJson<DeferSuggestion>(response, 'Load defer suggestion');
}

export async function fetchExceptions(
  filters: ExceptionFilters = {},
  signal?: AbortSignal,
): Promise<ReconciliationException[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.vendorId) params.set('vendorId', filters.vendorId);
  const qs = params.toString();
  const response = await fetch(`${BASE}/exceptions${qs ? `?${qs}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<ReconciliationExceptionListResponse>(response, 'Load exceptions');
  return body.items ?? [];
}

export async function resolveException(
  id: string,
  request: ResolveExceptionRequest,
  signal?: AbortSignal,
): Promise<ReconciliationException> {
  const response = await fetch(`${BASE}/exceptions/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<ReconciliationException>(response, 'Resolve exception');
}

// ---------------------------------------------------------------------------
// Import rows / batches / unmatched worklist (drill-down read endpoints)
// ---------------------------------------------------------------------------

export interface ImportRowFilters {
  batchId?: string;
  matchStatus?: ImportRowMatchStatus;
  vendorId?: string;
  top?: number;
  skip?: number;
}

export async function fetchUnmatchedWorklist(
  params: { batchId?: string; vendorId?: string },
  signal?: AbortSignal,
): Promise<UnmatchedWorklist> {
  const qs = new URLSearchParams();
  if (params.batchId) qs.set('batchId', params.batchId);
  if (params.vendorId) qs.set('vendorId', params.vendorId);
  const query = qs.toString();
  const response = await fetch(`${BASE}/unmatched-worklist${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  return readJson<UnmatchedWorklist>(response, 'Load unmatched worklist');
}

export async function fetchImportRows(
  filters: ImportRowFilters = {},
  signal?: AbortSignal,
): Promise<VendorImportRow[]> {
  const qs = new URLSearchParams();
  if (filters.batchId) qs.set('batchId', filters.batchId);
  if (filters.matchStatus) qs.set('matchStatus', filters.matchStatus);
  if (filters.vendorId) qs.set('vendorId', filters.vendorId);
  if (filters.top != null) qs.set('top', String(filters.top));
  if (filters.skip != null) qs.set('skip', String(filters.skip));
  const query = qs.toString();
  const response = await fetch(`${BASE}/import-rows${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<VendorImportRowListResponse>(response, 'Load import rows');
  return body.items ?? [];
}

/** Dormant (suspended/expired/disabled, not near renewal) import rows for a
 *  vendor's latest batch (or a specific batch) — the Inactive area. Parked by the
 *  engine: never breaks/variances/worklist entries. */
export async function fetchInactiveRows(
  params: { vendorId?: string; batchId?: string } = {},
  signal?: AbortSignal,
): Promise<VendorImportRow[]> {
  const qs = new URLSearchParams();
  if (params.vendorId) qs.set('vendorId', params.vendorId);
  if (params.batchId) qs.set('batchId', params.batchId);
  const query = qs.toString();
  const response = await fetch(`${BASE}/inactive-rows${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<VendorImportRowListResponse>(response, 'Load inactive rows');
  return body.items ?? [];
}

/** Active-but-free trial import rows (25-seat Microsoft NCE trials) for a
 *  vendor's latest batch (or a specific batch) — the Trials area. Parked by the
 *  engine: never breaks/variances/worklist entries. Rows with `trialAutoRenews`
 *  will convert to billing at renewal and warrant an operator's attention. */
export async function fetchTrialRows(
  params: { vendorId?: string; batchId?: string } = {},
  signal?: AbortSignal,
): Promise<VendorImportRow[]> {
  const qs = new URLSearchParams();
  if (params.vendorId) qs.set('vendorId', params.vendorId);
  if (params.batchId) qs.set('batchId', params.batchId);
  const query = qs.toString();
  const response = await fetch(`${BASE}/trial-rows${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<VendorImportRowListResponse>(response, 'Load trial rows');
  return body.items ?? [];
}

/** Forward-looking renewal rows (Control 3): current + scheduled blocks, for a
 *  vendor's latest renewal batch (or a specific batch). Optional matchStatus. */
export async function fetchRenewalRows(
  params: { vendorId?: string; batchId?: string; matchStatus?: ImportRowMatchStatus } = {},
  signal?: AbortSignal,
): Promise<VendorImportRenewalRow[]> {
  const qs = new URLSearchParams();
  if (params.vendorId) qs.set('vendorId', params.vendorId);
  if (params.batchId) qs.set('batchId', params.batchId);
  if (params.matchStatus) qs.set('matchStatus', params.matchStatus);
  const query = qs.toString();
  const response = await fetch(`${BASE}/renewal-rows${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<VendorImportRenewalRowListResponse>(response, 'Load renewal rows');
  return body.items ?? [];
}

export interface ReconcileResult {
  /** Present when the engine finished within the request window. */
  processed?: number;
  results?: unknown[];
  /** True (HTTP 202) when the engine is still processing in the background. */
  running?: boolean;
  message?: string;
}

/** Re-run the orchestrator reconciliation engine (admin only). Pass `vendorId`
 *  to re-evaluate that vendor's current latest batch (resolved live, so a stale
 *  batch id can't 404); `batchId` targets a specific batch. `onlySku` /
 *  `onlyCustomer` scope the run to just the rows for that SKU / customer name
 *  (after creating a vendor product / alias) so only the affected records are
 *  touched. */
export async function triggerReconcile(
  params: { vendorId?: string; batchId?: string; onlySku?: string; onlyCustomer?: string } = {},
  signal?: AbortSignal,
): Promise<ReconcileResult> {
  const payload: Record<string, string> = {};
  if (params.vendorId) payload.vendorId = params.vendorId;
  if (params.batchId) payload.batchId = params.batchId;
  if (params.onlySku) payload.onlySku = params.onlySku;
  else if (params.onlyCustomer) payload.onlyCustomer = params.onlyCustomer;
  const response = await fetch(`${BASE}/reconcile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return readJson<ReconcileResult>(response, 'Re-run reconcile');
}

export async function fetchImportBatches(
  params: { vendorId?: string; top?: number } = {},
  signal?: AbortSignal,
): Promise<VendorImportBatch[]> {
  const qs = new URLSearchParams();
  if (params.vendorId) qs.set('vendorId', params.vendorId);
  if (params.top != null) qs.set('top', String(params.top));
  const query = qs.toString();
  const response = await fetch(`${BASE}/import-batches${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<VendorImportBatchListResponse>(response, 'Load import batches');
  return body.items ?? [];
}
